import React, { FC } from 'react';
import 'reflect-metadata';
import * as RoutesModule from 'Routes';
import './index.css';
import { Router } from 'react-router';
import { ConfigurationService } from 'services/ConfigurationService';
import { AxiosServiceFactory, AxiosService } from 'services/AxiosServiceFactory';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { SessionVerificationApiService } from 'services/data-services/SessionVerificationService';
import { createBrowserHistory } from 'history';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { TeamStore } from 'stores/TeamStore';
import { UiStore } from 'stores/UiStore';
import { Provider } from 'services/ioc/ioc.react';
import { LogService } from 'services/LogService';
import { TrackingContextProvider } from 'services/helper-services/TrackingContextProvider';
import { ThemeProviderProps, ThemeProvider as _ThemeProvider } from '@trackman/web-shared-components';
import { SessionPlayStore } from './stores/SessionPlayStore';
import { ServiceTypes } from './constants/ServiceTypes';
import { container } from './services/ioc/ioc';
import * as serviceWorker from './serviceWorker';
import App from './App';
import { ColumnsStore } from 'stores/ColumnsStore';
import { AutosaveStore } from 'stores/AutosaveStore';
import { SecondSourceService } from 'services/data-services/SecondSourceService';
import { createRoot } from 'react-dom/client';
import SnackbarStore from 'stores/SnackbarStore';
import { ClipboardService } from 'services/ClipboardService';
import { SessionLockFeedService } from 'services/data-services/SessionLockFeedService';
import { CustomerCSVConfigurationService } from 'services/data-services/CustomerCSVConfigurationService';
import { CustomerCSVConfigurationStore } from 'stores/CustomerCSVConfigurationStore';
import { FilterStore } from 'stores/FilterStore';

const ThemeProvider = _ThemeProvider as unknown as FC<ThemeProviderProps & { children?: React.ReactNode }>;

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();

function reactRender(): void {
  const history = createBrowserHistory();

  const root = createRoot(document.getElementById('root')!);

  root.render(
    <ThemeProvider>
      <Provider container={container}>
        <Router history={history}>
          <App>{RoutesModule.routes}</App>
        </Router>
      </Provider>
    </ThemeProvider>
  );
}

async function renderApp(): Promise<void> {
  const configurationService = new ConfigurationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance(ServiceTypes.ApiService);
  const logService = new LogService(apiServiceAxiosInstance, new TrackingContextProvider());
  const columnsStore = new ColumnsStore();

  container.bind<LogService>(ServiceTypes.LogService).toDynamicValue(() => logService);

  const authenticationService = new AuthenticationService();

  const errorHandler = new AxiosErrorHandler(logService);
  const sessionVerifierApiService = new SessionVerificationApiService(apiServiceAxiosInstance, logService);
  const sessionVerifierStore = new SessionVerificationStore(sessionVerifierApiService);
  const teamStore = new TeamStore(sessionVerifierApiService);
  const autosaveStore = new AutosaveStore();
  const playStore = new SessionPlayStore(sessionVerifierApiService, autosaveStore);
  const secondSourceService = new SecondSourceService(apiServiceAxiosInstance, logService);
  const snackbarStore = new SnackbarStore();
  const cliboardService = new ClipboardService(snackbarStore);
  const customerCSVConfigurationService = new CustomerCSVConfigurationService(apiServiceAxiosInstance, logService);
  const customerCSVConfigurationStore = new CustomerCSVConfigurationStore(customerCSVConfigurationService);
  const filterStore = new FilterStore();

  container
    .bind<CustomerCSVConfigurationService>(ServiceTypes.CustomerCSVConfigurationService)
    .toDynamicValue(() => customerCSVConfigurationService);
  container
    .bind<CustomerCSVConfigurationStore>(ServiceTypes.CustomerCSVConfigurationStore)
    .toDynamicValue(() => customerCSVConfigurationStore);

  const uiStore = new UiStore();
  container.bind<AxiosService>(ServiceTypes.ApiService).toDynamicValue(() => apiServiceAxiosInstance);
  container.bind<AxiosService>(ServiceTypes.SessionVerificationApiService).toDynamicValue(() => sessionVerifierApiService);
  container.bind<SessionVerificationStore>(ServiceTypes.SessionVerificationStore).toDynamicValue(() => sessionVerifierStore);
  container.bind<ConfigurationService>(ServiceTypes.ConfigurationService).to(ConfigurationService).inSingletonScope();
  container.bind<AuthenticationService>(ServiceTypes.AuthenticationService).to(AuthenticationService).inSingletonScope();
  container.bind<TeamStore>(ServiceTypes.TeamStore).toDynamicValue(() => teamStore);
  container.bind<SessionPlayStore>(ServiceTypes.SessionPlayStore).toDynamicValue(() => playStore);
  container.bind<UiStore>(ServiceTypes.UiStore).toDynamicValue(() => uiStore);
  container.bind<ColumnsStore>(ServiceTypes.ColumnsStore).toDynamicValue(() => columnsStore);
  container.bind<AutosaveStore>(ServiceTypes.AutosaveStore).toDynamicValue(() => autosaveStore);
  container.bind<SecondSourceService>(ServiceTypes.SecondSourceService).toDynamicValue(() => secondSourceService);
  container
    .bind<SnackbarStore>(ServiceTypes.SnackbarStore)
    .toDynamicValue(() => snackbarStore)
    .inSingletonScope();
  container
    .bind<ClipboardService<any>>(ServiceTypes.ClipboardService)
    .toDynamicValue(() => cliboardService)
    .inSingletonScope();
    container.bind<FilterStore>(ServiceTypes.FilterStore).toDynamicValue(() => filterStore);

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(apiServiceAxiosInstance, authenticationService, errorHandler);
  reactRender();
}

renderApp();
